import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, FileSpreadsheet, UploadCloud } from "lucide-react";
import { useState, type FormEvent } from "react";
import { api, formatApiError } from "../api/client";
import { ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";
import { formatNumber } from "../lib/format";

type Unit = { id: string; name_ar: string; status: string };
type Preview = { id: string; totalRows: number; validRows: number; duplicateRows: number; errorRows: number };

export function ImportsPage() {
  const [preview, setPreview] = useState<Preview | null>(null);
  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api<Unit[]>("/units/tree")).data
  });
  const upload = useMutation({
    mutationFn: async (form: FormData) =>
      (await api<Preview>("/imports", { method: "POST", body: form })).data,
    onSuccess: setPreview
  });
  const confirm = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error("لا توجد معاينة جاهزة.");
      return (await api<{ insertedRows: number }>(`/imports/${preview.id}/confirm`, { method: "POST" })).data;
    }
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    upload.mutate(new FormData(event.currentTarget));
  }
  return (
    <div className="page">
      <PageHeader
        title="استيراد الأسر"
        description="ارفع ملف Excel أو CSV، راجع النتائج، ثم أكّد الإدخال. لن تُحفظ الصفوف المكررة أو غير الصالحة."
      />
      <section className="stepper" aria-label="مراحل الاستيراد">
        <div className="step step--active">
          <span>١</span>رفع الملف
        </div>
        <div className={`step ${preview ? "step--active" : ""}`}>
          <span>٢</span>مراجعة
        </div>
        <div className={`step ${confirm.isSuccess ? "step--active" : ""}`}>
          <span>٣</span>تأكيد
        </div>
      </section>
      {!preview ? (
        <section className="content-card upload-card">
          <FileSpreadsheet aria-hidden="true" />
          <div>
            <h2>ملف بيانات الأسر</h2>
            <p>الحد الأقصى 1000 صف و5 ميجابايت. عناوين مقبولة: الاسم، الهاتف، الهوية، عدد الأفراد.</p>
          </div>
          {units.isPending ? (
            <LoadingState />
          ) : units.isError ? (
            <ErrorState error={units.error} />
          ) : (
            <form className="form-grid" onSubmit={submit}>
              <Field label="الوحدة المستهدفة" htmlFor="import-unit">
                <select id="import-unit" name="unitId" required defaultValue="">
                  <option value="" disabled>
                    اختر الوحدة
                  </option>
                  {units.data
                    .filter((unit) => unit.status === "active")
                    .map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name_ar}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="ملف Excel أو CSV" htmlFor="import-file" hint="لن يتم إدخال أي سجل قبل التأكيد.">
                <input id="import-file" name="file" type="file" accept=".xlsx,.xls,.csv" required />
              </Field>
              {upload.isError ? (
                <div className="inline-alert form-span" role="alert">
                  {formatApiError(upload.error)}
                </div>
              ) : null}
              <button className="button form-span" type="submit" disabled={upload.isPending}>
                <UploadCloud aria-hidden="true" />
                {upload.isPending ? "جارٍ تحليل الملف…" : "رفع ومعاينة"}
              </button>
            </form>
          )}
        </section>
      ) : (
        <section className="content-card">
          <div className="section-head">
            <div>
              <h2>نتيجة المعاينة</h2>
              <p>لم تُضف البيانات إلى سجل الأسر بعد.</p>
            </div>
            <StatusBadge status={confirm.isSuccess ? "completed" : "ready"} />
          </div>
          <div className="summary-grid">
            <div>
              <span>إجمالي الصفوف</span>
              <strong>{formatNumber(preview.totalRows)}</strong>
            </div>
            <div className="success">
              <span>صالحة</span>
              <strong>{formatNumber(preview.validRows)}</strong>
            </div>
            <div className="warning">
              <span>مكررة محتملة</span>
              <strong>{formatNumber(preview.duplicateRows)}</strong>
            </div>
            <div className="danger">
              <span>بها أخطاء</span>
              <strong>{formatNumber(preview.errorRows)}</strong>
            </div>
          </div>
          {confirm.isSuccess ? (
            <div className="success-panel" role="status">
              <CheckCircle2 aria-hidden="true" />
              <div>
                <strong>اكتمل الاستيراد</strong>
                <p>أضيفت {formatNumber(confirm.data.insertedRows)} أسرة بنجاح.</p>
              </div>
            </div>
          ) : (
            <>
              <p className="notice">
                سيتم استيراد الصفوف الصالحة فقط. راجع المكررات المحتملة في شاشة الأسر قبل دمج أي سجل.
              </p>
              {confirm.isError ? (
                <div className="inline-alert" role="alert">
                  {formatApiError(confirm.error)}
                </div>
              ) : null}
              <div className="dialog-actions">
                <button className="button button--secondary" type="button" onClick={() => setPreview(null)}>
                  اختيار ملف آخر
                </button>
                <button
                  className="button"
                  type="button"
                  onClick={() => confirm.mutate()}
                  disabled={confirm.isPending || preview.validRows === 0}
                >
                  {confirm.isPending
                    ? "جارٍ الإدخال…"
                    : `تأكيد استيراد ${formatNumber(preview.validRows)} سجل`}
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}
